import { action, observable } from 'mobx';

import { iconSetNames } from '@estee/elc-icons';
import { IAppConfig } from '@estee/elc-universal-utils';

import {
    IamType,
    OTP_TYPE_EMAIL,
    OTP_TYPE_EMAIL_SMS,
    OTP_TYPE_SMS,
    OTP_TYPE_SMS_EMAIL,
    OtpType,
    rampCommonPath,
    UserIdentifier
} from '~constants/Constants';
import { SignInType } from '~constants/SignInTypeEnum';

export type PrimitiveValueType = string | number | boolean;

export interface IDropdownOption {
    label: string;
    value: string;
}

export interface IDropDownOptionConfig {
    label?: string;
    labelKey?: string;
    value: string;
}
export interface ICustomValidation {
    [key: string]: unknown;
    // Custom validator name registered to FormValidator
    validator: string;
}

export type FieldValueType = 'number' | 'string' | 'boolean';

export interface IFieldValidationsConfig {
    type: FieldValueType;
    validations?: (string | ICustomValidation)[];
    // Defines static options for select input, or valid values for validation only.
    options?: { value: PrimitiveValueType; label?: string; labelKey?: string }[];
}

export interface IFieldsValidations {
    [key: string]: IFieldValidationsConfig;
}

export interface IUserInfoDisplay {
    name: string;
    email: string;
    userIdentifier?: IUserIdentifier;
}

interface IUserIdentifier {
    identifier?: `${UserIdentifier}`;
    codeType?: string;
}

// A static value, or string with placeholders or a compound object containing both
export type AdditionalRequestFieldType = string | number | boolean;

export interface IRequestFieldsAdditionalMappings {
    [key: string]: PrimitiveValueType;
}

export type InputType =
    | 'select'
    | 'selectCms'
    | 'selectApi'
    | 'text'
    | 'tel'
    | 'label'
    | 'fieldset'
    | 'checkboxGroup'
    | 'accordionCheckboxGroup'
    | 'checkbox'
    | 'dateDay'
    | 'dateMonth'
    | 'dateYear'
    | 'email'
    | 'password'
    | 'passwordStrength'
    | 'passwordCriteria'
    | 'textLink'
    | 'forgotPassword'
    | 'termsAndConditions'
    | 'privacyNotice';
// Validator name and error message mapping
export interface IValidatorMessages {
    [key: string]: string;
}

export interface IFormItem {
    // For text only label, name is also required so that we can customize style easily.
    name: string;
    inputType: InputType;
    labelKey: string;
    inputLabel?: string;
    hiddenText?: string;
    enableChecker?: boolean;
    errorMessages?: IValidatorMessages;
    readonly?: boolean;
    // For select or input, string is the only value type allowed.
    initialValue?: string | boolean;
}

export interface ICheckableItem extends IFormItem {
    inverseCheckedValue?: boolean;
    initialValue?: boolean;
    // We use `enabled` instead of `disabled` because eventually this prop will go through to elc-checkbox component.
    // Checkbox is by default enabled if this prop is missing, set it to false to explicity disable it
    enabled?: boolean;
}

export interface ISelectAll {
    labelKey: string;
    name: string;
    /**
     * @description default to `bottom` if not set
     */
    position?: 'top' | 'bottom';
    collapse?: boolean;
}

export interface ICheckboxGroup extends Omit<IFormItem, 'initialValue'> {
    inverseCheckedValue?: boolean;
    selectAll?: ISelectAll;
    items: Omit<ICheckableItem, 'inputType'>[];
}

export function isCheckboxGroup(formItem: InputSchemaType): formItem is ICheckboxGroup {
    return (
        (formItem.inputType === 'checkboxGroup' ||
            formItem.inputType === 'accordionCheckboxGroup') &&
        formItem.hasOwnProperty('items')
    );
}

export function isCmsSelect(formItem: InputSchemaType): formItem is ICmsSelect {
    return formItem.inputType === 'selectCms';
}

export interface IFieldset extends Omit<IFormItem, 'initialValue' | 'errorMessages'> {
    items: IFormItem[];
}

export function isFieldset(formItem: InputSchemaType): formItem is IFieldset {
    return formItem.inputType === 'fieldset' && formItem.hasOwnProperty('items');
}

export function isPasswordStrength(formItem: InputSchemaType): formItem is IPasswordStrength {
    return formItem.inputType === 'passwordStrength';
}

export function isPasswordCriteria(formItem: InputSchemaType): formItem is IPasswordCriteria {
    return formItem.inputType === 'passwordCriteria';
}

export interface ISelectSource {
    type: string;
    value: string;
}
export interface ISelect extends IFormItem {
    source?: ISelectSource;
    // Values of options that should be displayed on top.
    pin?: string[];
    staticLookupName?: string;
    dependsOnField?: string;
    // Set additional form values with other properties.
    additionalFormValues?: string[];
    withFloatingLabel?: boolean;
}

interface ICmsSelect extends ISelect {
    source: ISelectSource;
}

export interface IApiSelectSource extends ISelectSource {
    triggerLength: number;
    brandRegionQuery?: boolean;
    // Since jsonpath is removed, path like 'suggestions.[2].subtree' is no longer supported;
    resultPath?: string;
    formatLabel: string;
}

export interface IApiSelect {
    source: IApiSelectSource;
}

interface IDaySelect extends ISelect {
    yearFieldName: string;
    monthFieldName: string;
}

interface IDateSelect extends ISelect {
    offsets?: number[];
}

export interface IPasswordStrengthItem {
    name: string;
    labelKey: string;
    inputLabel: string;
}

export interface IPasswordCriteriaItem extends IPasswordStrengthItem {
    pattern: string;
}

export interface IPasswordStrength extends IFormItem {
    name: string;
    inputLabel: string;
    items: IPasswordStrengthItem[];
    passwordFieldName: string;
}

export interface IPasswordCriteria extends IPasswordStrength {
    items: IPasswordCriteriaItem[];
}

export type InputSchemaType =
    | ICheckboxGroup
    | ICheckableItem
    | ISelect
    | IDateSelect
    | IDaySelect
    | IFormItem
    | IPasswordStrength
    | IPasswordCriteria;

export interface IFormSection {
    name?: string;
    titleLabelKey?: string;
    items: InputSchemaType[];
}

export interface StaticLookupUrls {
    [key: string]: string;
}

export interface IFormSchema {
    sections: IFormSection[];
}

interface IFeatureOptions {
    enabled?: boolean;
    omitHttpMethods: string[];
}

export enum UserIdType {
    MOBILE_NUMBER = 'mobileNumber',
    EMAIL_ADDRESS = 'emailAddress',
    PC_EMAIL_ADDRESS = 'pcEmailAddress'
}

export const otpTypeMap = new Map([
    [OTP_TYPE_SMS, [UserIdType.MOBILE_NUMBER]],
    [OTP_TYPE_EMAIL, [UserIdType.EMAIL_ADDRESS]],
    [OTP_TYPE_EMAIL_SMS, [UserIdType.MOBILE_NUMBER, UserIdType.EMAIL_ADDRESS]],
    [OTP_TYPE_SMS_EMAIL, [UserIdType.MOBILE_NUMBER, UserIdType.EMAIL_ADDRESS]]
]);

export interface IFormConfig {
    additionalTermsLabelKey?: string;
    badCharValidation: string;
    schema: IFormSchema;
    fieldsValidations: IFieldsValidations;
    postRequestFields: string[];
    putRequestFields: string[];
    requestFieldsAdditionalMappings: IRequestFieldsAdditionalMappings;
    userInfoDisplay?: IUserInfoDisplay;
    staticLookupUrls?: StaticLookupUrls;
    turnOffOtp?: boolean;
    features: {
        posFrontiers?: IFeatureOptions;
        updateUserAddress?: IFeatureOptions;
        updateUserLoyalty?: IFeatureOptions;
        allowPartialOfflineUserEdit?: IFeatureOptions;
    };
    // For some region, once identifier field is filled with value from 3rd party social platform, it should become readonly.
    identifierReadonlyOnFill?: boolean;
    identifiers: UserIdType[];
    submitJourney?: string;
    redirectButtonLabel?: string;
}

export interface ISocialSignUpProvider {
    providerParams: string[];
    providerName: string;
    displayName: string;
    messageKey: string;
    registerSuccess: {
        messageKey: string;
        supportingTextKey: string;
        submitButtonLabelKey: string;
        redirectUrl?: RedirectUrl;
    };
    profileError: {
        ctaLabelKey?: string;
        instructionLabelKey?: string;
    };
}

export interface RedirectUrl {
    pc: string;
    mobile: string;
}

export interface IRampAppConfig extends IAppConfig {
    // from Drupal/ecommConfig
    pgApiTokens?: {
        rampForm?: string;
    };
}

export interface IVulcanConfig {
    env: string;
    perlgem: {
        regionId: number;
        brandId: number;
    };
    stardust: { buid: string };
}

export interface IDataConfig {
    facebookAppId?: string | null;
    showLoginForm?: boolean;
}

export interface ISocialOauthConfig {
    responseType: string;
    /** The application id from 3rd-party platform */
    clientId: string;
    /** The authentication url from 3rd-party platform */
    requestUrl: string;
    scope: string;
    /** Display language for Login and consent screens of social provider */
    uiLocales?: string;
    // Displays an option to add a LINE Official Account as a friend during login.
    botPrompt?: string;
}

export interface ISocialProvider {
    providerName: SignInType;
    iconSetName?: iconSetNames;
    displayName?: string;
    buttonLabelKey?: string;
    oauthConfig: ISocialOauthConfig;
}

export type SocialWrapperPosition = 'top' | 'bottom';
export type ButtonStyleType = 'rect' | 'circle' | 'radio';
export enum SocialComponent {
    DIVIDER = 'divider',
    MESSAGE = 'message',
    BUTTONS = 'buttons'
}

export interface ISocialButtonWrapperConfig {
    buttonStyle: ButtonStyleType;
    wrapperPosition: SocialWrapperPosition;
    wrapperLayout: SocialComponent[];
    messageLabelKey?: string;
    dividerLabelKey?: string;
    excludedUseCases?: IamType[];
    providers: ISocialProvider[];
}

export interface IOtpConfig {
    otpLength: number;
    otpType: OtpType;
    enableCountdown?: boolean;
    resendCodeBlockThreshold?: number;
    fraudVerificationEnabled?: boolean;
}

export interface IIamCommonConfig {
    socialProviders: ISocialButtonWrapperConfig;
    socialSignUpProviders?: { providers: ISocialSignUpProvider[] };
    otp?: IOtpConfig;
    fraudVerificationEnabled?: boolean;
    badCharValidation?: string;
    commonEmailDomains?: string[];
    mobileEmailDomains?: string[];
    siteType?: 'ecommerce' | 'coresite';
    facebookAppId?: string;
}

export interface IRampPreloadedConfig extends IRampAppConfig {
    // from BUID configuration
    rampForm?: IFormConfig;
    common?: IIamCommonConfig;
    [rampCommonPath]?: IIamCommonConfig;
}

export interface IIamConfig {
    signIn: IFormConfig;
    signUp: IFormConfig;
    guestCheckout: IFormConfig;
}

export class Config {
    @observable public config: IRampPreloadedConfig = {};

    constructor(config: IAppConfig) {
        this.updateConfig(config);
    }

    @action
    public updateConfig(config: Partial<IRampPreloadedConfig>) {
        this.config = { ...this.config, ...config };
    }
}
